import { User } from '/@/modules/user/models/User';
import { AuthUserPermission } from '/@/modules/auth/models/AuthUserPermission';
import { Announcement } from '@/modules/announcement/models/Announcement';
import { CurrentEstate } from '/@/modules/estate/models/CurrentEstate';
import { EstateSignUp } from '/@/modules/estate/models/EstateSignUp';
import { ResolvedUserPermissionSet } from '@/modules/auth/models/ResolvedUserPermissionSet';
import { getOwnProperties } from '@/modules/core/app/utils/ObjectUtil';
import { HubSpotUser } from '@/modules/user/models/HubSpotUser';
import { ResourceCenter } from '@/modules/resourcecenter/models/ResourceCenter';
import { OtpType } from '@/modules/otp/enums/OtpType';
import { PlatformPaywall } from '@/modules/platformproduct/models/PlatformPaywall';
import { Team } from '@/modules/team/models/Team';
import { AuthUserRoute } from '@/modules/auth/routes/auth.routes';
import { OutlookAccount } from '@/modules/auth/models/OutlookAccount';

export class AuthUser extends User {
  /**
   * @type {CurrentEstate|null}
   */
  currentEstate;

  /**
   * @type {Team|undefined}
   */
  currentTeam;

  /**
   * @type {AuthUserPermission}
   */
  authUserPermission;

  /**
   * @type {ResolvedUserPermissionSet}
   */
  resolvedUserPermissionSet;

  /**
   * @type {EstateSignUp}
   */
  estateSignUp;

  /**
   * @type {HubSpotUser}
   */
  hubSpotUser;

  /**
   * @type {number}
   */
  impersonatorId;

  /**
   * @var {boolean}
   */
  isViewingEstateDetails;

  /**
   * @var {boolean}
   */
  hasViewedClaraSplashScreen;

  hasViewedClaraForPlannersLauncher;

  /**
   * @var {boolean}
   */
  hasLatestPolicyConsent;

  /**
   * @type {Announcement[]}
   */
  unseenAnnouncements;

  /**
   * @type {PlatformPaywall}
   */
  platformPaywall;

  /**
   * @var {boolean}
   */
  hasSocialAccounts;

  /**
   * @var {boolean}
   */
  isPayingCustomer;

  /**
   * @type {OutlookAccount|null}
   */
  outlookAccount;

  constructor(model = {}) {
    super(model);

    this.hasSocialAccounts = model.hasSocialAccounts;
    this.isPayingCustomer = model.isPayingCustomer ?? false;
    this.outlookAccount = OutlookAccount.mapModelOrNull(model.outlookAccount);

    this.currentEstate = CurrentEstate.mapModelOrNull(model.currentEstate);
    this.currentTeam = Team.mapModelOrNull(model.currentTeam);
    this.estateSignUp = EstateSignUp.mapModelOrNull(model.estateSignUp);
    this.impersonatorId = model.impersonatorId;
    this.hubSpotUser = HubSpotUser.mapModelOrNull(model.hubspotUser);
    this.isViewingEstateDetails = model.isViewingEstateDetails ?? false;
    this.resourceCenter = ResourceCenter.mapModelOrNull(model.resourceCenter);
    this.hasViewedClaraSplashScreen = model.hasViewedClaraSplashScreen;
    this.hasViewedClaraForPlannersLauncher = model.hasViewedClaraForPlannersLauncher;
    this.hasLatestPolicyConsent = model.hasLatestPolicyConsent;
    this.unseenAnnouncements = Announcement.mapModels(model.unseenAnnouncements);
    this.platformPaywall = PlatformPaywall.mapModel(model.platformPaywall);

    this.mapPermission(model);
  }

  mapPermission(model = {}) {
    // checking model.keys for backward compatibility
    this.authUserPermission = model.permission?.keys
      ? new AuthUserPermission(model.permission)
      : null;
    this.resolvedUserPermissionSet = !this.authUserPermission
      ? ResolvedUserPermissionSet.mapModel(model.permission)
      : null;
  }

  /**
   * @returns {AuthUserPermission|undefined|AuthUserPermission}
   */
  get permission() {
    // backward compatibility
    if (this.authUserPermission) {
      return this.authUserPermission;
    }

    // ClearDesk
    if (this?.isViewingEstateDetails || !this.currentEstate) {
      return this.resolvedUserPermissionSet?.clearDesk;
    }

    // logged in to estate
    if (this.currentEstate?.isClearEstate) {
      return this.resolvedUserPermissionSet?.clearEstate;
    }

    // logged in to vault
    if (this.currentEstate?.isClearVault) {
      return this.resolvedUserPermissionSet?.clearVault;
    }

    return [];
  }

  // backwards compatibility
  set permission(value) {
    this.authUserPermission = value;
  }

  /**
   * @param estate {Estate}
   * @returns {AuthUserPermission|undefined}
   */
  getPermission(estate = null) {
    if (this.authUserPermission) {
      return this.authUserPermission;
    }

    // ClearDesk
    if (this?.isViewingEstateDetails) {
      if (estate) {
        return estate?.isClearEstate
          ? this.resolvedUserPermissionSet?.clearEstate
          : this.resolvedUserPermissionSet?.clearVault;
      }
      return this.resolvedUserPermissionSet?.clearDesk;
    }

    return this.permission;
  }

  /**
   * @returns {User}
   */
  get user() {
    const userObject = { ...this };
    getOwnProperties(this).forEach((property) => {
      delete userObject[property];
    });

    return User.mapModel(userObject);
  }

  /**
   * @return {boolean}
   */
  get hasPlannerRole() {
    return !!this.estateAccount?.isPlanner;
  }

  /**
   * @return {boolean}
   */
  get hasExecutorRole() {
    return !!this.estateAccount?.isExecutor;
  }

  /**
   * @return {boolean}
   */
  get hasOnlyBeneficiaryRole() {
    return !!this.estateAccount?.isOnlyBeneficiary;
  }

  /**
   * @return {boolean}
   */
  get hasBeneficiaryRole() {
    return !!this.estateAccount?.isBeneficiary;
  }

  /**
   * @return {boolean}
   */
  get isLegalGuardian() {
    return !!this.estateAccount?.isLegalGuardian;
  }

  /**
   * @return {boolean}
   */
  get hasEstateProfessionalRole() {
    return !!this.estateAccount?.isEstateProfessional;
  }

  /**
   * @return {boolean}
   */
  get isAdminEstateProfessional() {
    return !!this.professional?.isEstateProfessional && !!this.isAdmin;
  }

  /**
   * @return {boolean}
   */
  get isProfessional() {
    return !!this.professional;
  }

  /**
   * @return {boolean}
   */
  get isNotary() {
    return !!this.professional?.isNotary;
  }

  /**
   * @return {boolean}
   */
  get isPartnerNotary() {
    return !!this.professional?.isNotary && !!this.professional?.company?.isPartner;
  }

  /**
   * @returns {boolean}
   */
  get hasCurrentEstate() {
    return !!this.currentEstate;
  }

  /**
   * @return {boolean}
   */
  get isViewingEstate() {
    // ex: This is true when viewing and estate as a professional
    return this.permission.canManageEstate && this.currentEstate && !this.isViewingEstateDetails;
  }

  /**
   * @return {boolean}
   */
  get isManagingEstates() {
    // ex: This is true when not viewing an estate nor viewing the details modal
    return this.permission.canManageEstate && !this.currentEstate && !this.isViewingEstateDetails;
  }

  /**
   * @return {boolean}
   */
  get isImpersonated() {
    return !!this.impersonatorId;
  }

  /**
   * @return {boolean}
   */
  get isInternal() {
    return this.isSuperAdmin || (this.isEstateManager && this.isStaff);
  }

  /**
   * @return {boolean}
   */
  get needsTriage() {
    if (this.homePage === AuthUserRoute.ONBOARDING.name) {
      return true;
    }

    if (this.estateSignUp?.needsTriage(this.hasCurrentEstate)) {
      return true;
    }

    // https://www.notion.so/clearestate/CEC-2-562a9591307143d5886e63518ba274bc
    if (!this.permission.canAccessTriage || this.isImpersonated) {
      return false;
    }

    if (this.currentEstate?.hasOnboardedAtLeastOneExecutor) {
      return false;
    }

    return true;
  }

  /**
   * @returns {number}
   */
  get estateRoleId() {
    return this.estateAccount?.roleId;
  }

  /**
   * @returns {function(): string}
   */
  get estateRoleName() {
    return this.estateAccount?.roleName;
  }

  get canLogClearDeskIssue() {
    return (this.isEstateProfessional || this.isJuniorEstateProfessional) && !this.isViewingEstate;
  }

  get canSetupOtp() {
    if (!this.permission.canAccessOtp) {
      return false;
    }

    return (
      (this.permission.canAccessSmsOtp || this.permission.canAccessEmailOtp) &&
      !this.otpContacts.allVerifiedContacts.length
    );
  }

  get needsOtpSetup() {
    if (
      !this.permission.canRequireOtp ||
      (!this.permission.canAccessSmsOtp && !this.permission.canAccessEmailOtp)
    ) {
      return false;
    }

    const hasSmsOtpSetup =
      this.permission.canAccessSmsOtp &&
      !!this.otpContacts.getVerifiedContactsByType(OtpType.PHONE_NUMBER).length;

    const hasEmailOtpSetup =
      this.permission.canAccessEmailOtp &&
      !!this.otpContacts.getVerifiedContactsByType(OtpType.EMAIL).length;

    return !hasSmsOtpSetup && !hasEmailOtpSetup;
  }

  get isAdvisorProUser() {
    return this.isInFinancialAdvisorProfessionGroup && !!this.permission?.canAccessAdvisorPro;
  }

  get canAdvisorAccessBilling() {
    return this.isAdvisorProUser && !this.currentEstate && this.permission?.canAccessBilling;
  }
}
